import { action, computed, makeObservable, observable, reaction, runInAction } from 'mobx';
import { ROTA } from '../dominio/Rota';
import Ambiente from '../util/Ambiente';
import Configuracao from '../util/Configuracao';
import Store from './Store';
import { LOCALSTORAGE_STORAGE } from '@/util/StorageUtil';

/**
 * Classe utilizada para guardar e manipular o estado da aplicação.
 * Deve-se criar uma para cada contexto ou entidade.
 */
class AppStore extends Store {
    /**
     * @see {@link App}
     */
    theme;

    /**
     * Contador utilizado para fornecer valores únicos para o atributo 'key', obrigatório nos arrays de componentes
     * Não deve ser observable
     */
    uniqueKey = 0;

    /**
     * @see {@link App#reactionCarregarApp}
     */
    carregarApp = false;

    /**
     * Contador de requisições ao backend utilizado na barra de progresso.
     */
    quantidadeRequisicoes = 0;

    larguraJanela;
    alturaJanela;
    tipoNavegacaoEntradaApp;
    backdropOpen;
    validarShodo;
    validarPJeOfficePro;
    instalacaoShodoValida;
    instalacaoPJeOfficeProValida;
    conectividadeOK;
    exibirTextoConectividadeOK;
    mostrarPainelProcessosDetalhes = true;

    /**
     * @type {undefined | NodeJS.Timeout}
     */
    static verificadorConectividade = undefined;

    constructor(props) {
        super(props);

        makeObservable(this, {
            carregarApp: observable,
            quantidadeRequisicoes: observable,
            larguraJanela: observable,
            alturaJanela: observable,
            tipoNavegacaoEntradaApp: observable,
            backdropOpen: observable,
            validarShodo: observable,
            validarPJeOfficePro: observable,
            instalacaoShodoValida: observable,
            instalacaoPJeOfficeProValida: observable,
            conectividadeOK: observable,
            exibirTextoConectividadeOK: observable,
            mostrarPainelProcessosDetalhes: observable,
            setValidarShodo: action,
            setValidarPJeOfficePro: action,
            initVerificadorConectividade: action,
            setConectividadeOk: action,
            abrirApp: action,
            fecharApp: action,
            requisicoesPendentes: computed,
            zerarRequisicoes: action,
            incrementarRequisicoes: action,
            decrementarRequisicoes: action,
            toggleMostrarPainelProcessosDetalhes: action
        });

        runInAction(() => {
            this.backdropOpen = false;
            this.validarShodo = true;
            this.instalacaoShodoValida = false;
            this.validarPJeOfficePro = false;
            this.instalacaoPJeOfficeProValida = false;
            this.conectividadeOK = true;
            this.exibirTextoConectividadeOK = !this.conectividadeOK;
            this.larguraJanela = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0);
            this.alturaJanela = Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0);
        });

        this.createReaction(
            reaction(
                () => this.quantidadeRequisicoes,
                () => {
                    if (this.quantidadeRequisicoes === 0) {
                        Ambiente.zerarRequisicoes();
                    }
                }
            )
        );
    }

    get storageType() {
        return LOCALSTORAGE_STORAGE;
    }

    inicializar() {
        super.inicializar();

        const navigation = window.performance.getEntriesByType('navigation');

        if (navigation) {
            this.tipoNavegacaoEntradaApp = navigation[0].type;
        }

        if (
            window.location.pathname === ROTA.ACOMPANHAMENTO_SESSAO.rota ||
            window.location.pathname === ROTA.TELA_ERRO.rota ||
            window.location.pathname === ROTA.ADMIN.rota
        ) {
            this.setValidarShodo(false);
            this.setValidarPJeOfficePro(false);
        }

        if (this.validarShodo) {
            this.validarInstalacaoShodo();
        }

        if (this.validarPJeOfficePro) {
            this.validarInstalacaoPJeOfficePro();
        }

        this.initVerificadorConectividade();
    }

    setValidarShodo(validar) {
        this.validarShodo = validar;
    }

    setValidarPJeOfficePro(validar) {
        this.validarPJeOfficePro = validar;
    }

    async validarInstalacaoShodo() {
        const instalacaoShodo = await this.assinaturaService.validarInstalacaoShodo();
        runInAction(() => (this.instalacaoShodoValida = instalacaoShodo));
    }

    async validarInstalacaoPJeOfficePro() {
        const instalacaoPJeOfficePro = await this.assinaturaService.validarInstalacaoPJeOfficePro();
        runInAction(() => (this.instalacaoPJeOfficeProValida = instalacaoPJeOfficePro));
    }

    initVerificadorConectividade() {
        if (AppStore.verificadorConectividade !== undefined) {
            clearInterval(AppStore.verificadorConectividade);
        }

        AppStore.verificadorConectividade = setInterval(
            () => this.setConectividadeOk(),
            Configuracao.INTERVALO_TEMPO_VERIFICACAO_CONECTIVIDADE_REDE
        );
    }

    setConectividadeOk() {
        this.exibirTextoConectividadeOK = !this.conectividadeOK;
        this.conectividadeOK = window.navigator.onLine;
    }

    abrirApp = () => {
        this.carregarApp = true;
    };

    fecharApp = () => {
        this.carregarApp = false;
    };

    get requisicoesPendentes() {
        return !!(this.quantidadeRequisicoes > 0 || this.sessaoStore.ordenandoProcessos);
    }

    zerarRequisicoes() {
        this.quantidadeRequisicoes = 0;
    }

    incrementarRequisicoes() {
        this.quantidadeRequisicoes++;
    }

    decrementarRequisicoes() {
        if (this.quantidadeRequisicoes > 0) {
            this.quantidadeRequisicoes--;
        }
    }

    toggleMostrarPainelProcessosDetalhes() {
        this.mostrarPainelProcessosDetalhes = !this.mostrarPainelProcessosDetalhes;
    }
}

export default AppStore;
